import { Body, Button, Icon } from '@assembly-js/design-system';
import { Auth } from 'aws-amplify';
import clsx from 'clsx';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UsersClient from 'src/clients/UsersClient';
import { PortalConfigContext, RouteContext } from 'src/context';
import { useSendAccessLinkMutation } from 'src/services/api/unauthApi';
import {
  getAmplifyAuthConfig,
  getAuthRequestInfo,
  GetSignInMetadata,
  SignInWithGoogle,
} from 'src/utils/AuthUtils';

import { StudioGetStarted } from './StudioGetStarted';
import { StudioPageFrame } from './StudioPageFrame';
import { STUDIO_PRIMARY_BUTTON_CLASS } from './studioStyles';

type StudioLoginStep =
  | 'email'
  | 'signing-in'
  | 'link-sent'
  | 'link-resent'
  | 'expired';

interface StudioLoginQueryParams {
  email?: string;
  magic_link?: string;
  reason?: string;
}

export interface StudioLoginProps {
  // Called once a magic-link sign-in succeeds so the parent login page can load
  // the user's workspaces and redirect (mirrors the classic OnboardingAuth flow).
  onAuthComplete: () => void;
}

// Renders the subtitle with the email address emphasised (darker), matching the
// mocks where the address stands out against the muted body copy.
const bodyWithEmail = (template: string, email: string): ReactNode => {
  const [before, ...rest] = template.split(email);
  if (rest.length === 0) {
    return template;
  }
  return (
    <>
      {before}
      <span className="text-black">{email}</span>
      {rest.join(email)}
    </>
  );
};

/**
 * Passwordless login for Studio (internal) users, matching the Studio login mocks.
 * The user requests a magic link by email (email → "check your email"); clicking
 * the emailed link returns here with `?magic_link=&email=` and signs them in via
 * Cognito's custom auth flow — no password. Expired/used links and resends have
 * their own screens.
 */
export const StudioLogin = ({ onAuthComplete }: StudioLoginProps) => {
  const { t } = useTranslation();
  const { query }: { query: StudioLoginQueryParams } = useContext(RouteContext);
  const portalConfig = useContext(PortalConfigContext);
  const [sendAccessLink] = useSendAccessLinkMutation();

  const magicLink = query.magic_link;
  const [email, setEmail] = useState(query.email ?? '');
  const [isBusy, setIsBusy] = useState(false);

  const linkAlreadyDead =
    query.reason === 'link_expired' || query.reason === 'link_used';
  const computeInitialStep = (): StudioLoginStep => {
    if (magicLink && query.email) {
      return linkAlreadyDead ? 'expired' : 'signing-in';
    }
    return 'email';
  };
  const [step, setStep] = useState<StudioLoginStep>(computeInitialStep);
  const signInAttempted = useRef(false);

  const runSignIn = async (linkEmail: string, link: string) => {
    try {
      const requestInfo = await getAuthRequestInfo();
      // The dashboard defaults to SRP/password auth; switch Amplify to the custom
      // (passwordless) auth flow before initiating the magic-link sign-in.
      Auth.configure(
        getAmplifyAuthConfig({
          baseAuthConfig: portalConfig.AWS.Auth,
          authenticationFlowType: 'CUSTOM_AUTH',
        }),
      );
      const cognitoUser = await Auth.signIn(linkEmail, undefined, {
        ...GetSignInMetadata(undefined, requestInfo),
        type: 'magicLink',
      });
      const authedUser = await Auth.sendCustomChallengeAnswer(
        cognitoUser,
        link,
        {
          ...GetSignInMetadata(undefined, requestInfo),
          type: 'magicLink',
        },
      );
      await UsersClient.setAppUserCookie(
        authedUser,
        'workspace_login',
        'workspaces',
        linkEmail,
      );
      window.localStorage.setItem(
        `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
        authedUser.getUsername(),
      );
      onAuthComplete();
    } catch (error) {
      // Invalid, expired, or already-used (single-use) link.
      console.error('studio magic link login failed', error);
      setStep('expired');
    }
  };

  // Complete the sign-in when arriving from the emailed link.
  useEffect(() => {
    if (
      signInAttempted.current ||
      step !== 'signing-in' ||
      !magicLink ||
      !query.email
    ) {
      return;
    }
    signInAttempted.current = true;
    void runSignIn(query.email, magicLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueWithGoogle = () => {
    void SignInWithGoogle('');
  };

  const sendLoginLink = async (
    targetEmail: string,
    nextStep: StudioLoginStep,
  ) => {
    setIsBusy(true);
    try {
      await sendAccessLink({
        email: targetEmail,
        accessType: 'internalLogin',
      }).unwrap();
      setEmail(targetEmail);
      setStep(nextStep);
    } finally {
      setIsBusy(false);
    }
  };

  const goToEmailStep = () => setStep('email');

  if (step === 'email') {
    return (
      <StudioGetStarted
        onContinueWithGoogle={handleContinueWithGoogle}
        onContinueWithEmail={(submittedEmail) =>
          sendLoginLink(submittedEmail, 'link-sent')
        }
        title={t(($) => $.page_onboarding.studio.login.title)}
        subtitle={t(($) => $.page_onboarding.studio.login.subtitle)}
        submitLabel={t(
          ($) => $.page_onboarding.studio.login.continue_with_email,
        )}
      />
    );
  }

  if (step === 'signing-in') {
    return (
      <StudioPageFrame>
        <Icon icon="AssemblyLogo" className="mb-3 h-8 w-8 text-black" />
        <Body className="font-studio text-2xl text-black text-center sm:text-[26px]">
          {t(($) => $.page_onboarding.studio.login.signing_in)}
        </Body>
      </StudioPageFrame>
    );
  }

  const messageScreens: Record<
    'link-sent' | 'link-resent' | 'expired',
    {
      title: string;
      body: ReactNode;
      primaryLabel: string;
      onPrimary: () => void;
      secondaryLabel: string;
      onSecondary: () => void;
    }
  > = {
    'link-sent': {
      title: t(($) => $.page_onboarding.studio.login.link_sent_title),
      body: bodyWithEmail(
        t(($) => $.page_onboarding.studio.login.link_sent_subtitle, { email }),
        email,
      ),
      primaryLabel: t(($) => $.page_onboarding.studio.login.resend_email),
      onPrimary: () => {
        void sendLoginLink(email, 'link-resent');
      },
      secondaryLabel: t(($) => $.page_onboarding.studio.login.return_to_login),
      onSecondary: goToEmailStep,
    },
    'link-resent': {
      title: t(($) => $.page_onboarding.studio.login.resent_title),
      body: bodyWithEmail(
        t(($) => $.page_onboarding.studio.login.resent_subtitle, { email }),
        email,
      ),
      primaryLabel: t(($) => $.page_onboarding.studio.login.send_again),
      onPrimary: () => {
        void sendLoginLink(email, 'link-resent');
      },
      secondaryLabel: t(
        ($) => $.page_onboarding.studio.login.use_different_email,
      ),
      onSecondary: goToEmailStep,
    },
    expired: {
      title: t(($) => $.page_onboarding.studio.login.expired_title),
      body: bodyWithEmail(
        t(($) => $.page_onboarding.studio.login.expired_subtitle, { email }),
        email,
      ),
      primaryLabel: t(($) => $.page_onboarding.studio.login.send_new_link),
      onPrimary: () => {
        void sendLoginLink(email, 'link-resent');
      },
      secondaryLabel: t(
        ($) => $.page_onboarding.studio.login.use_different_email,
      ),
      onSecondary: goToEmailStep,
    },
  };

  const screen = messageScreens[step];

  return (
    <StudioPageFrame>
      <Icon icon="AssemblyLogo" className="mb-3 h-8 w-8 text-black" />
      <Body className="font-studio text-2xl text-black text-center sm:text-[26px]">
        {screen.title}
      </Body>
      <Body
        size="lg"
        className="mb-0 mt-3 font-studio text-center text-[#808080]"
      >
        {screen.body}
      </Body>
      <div className="mt-12 flex w-full flex-col gap-[17px]">
        <Button
          type="button"
          variant="studio"
          className={clsx('h-12 w-full', STUDIO_PRIMARY_BUTTON_CLASS)}
          label={screen.primaryLabel}
          disabled={isBusy}
          loading={isBusy}
          onClick={screen.onPrimary}
        />
        <button
          type="button"
          onClick={screen.onSecondary}
          className="w-full cursor-pointer border-none bg-transparent text-center text-[14px] leading-[22px] text-black/50"
        >
          {screen.secondaryLabel}
        </button>
      </div>
    </StudioPageFrame>
  );
};
